import CacheDatabase from "./CacheDatabase";
import { CACHE_VERSION, SNAPSHOT_TTL_MS } from "./constants";
import {
    CacheSnapshot,
    buildSignature,
    isSnapshotCompatible,
    isSnapshotExpired,
} from "./CacheSnapshot";
import { isStructuredCacheEnabled } from "./CacheControl";

type ComposeInput = {
    layoutId: string;
    hash: string;
    cloneTransformHash?: string | null;
    layout: any;
    font_urls?: any[];
    clone_transform?: any;
    clone_transforms?: any[];
    channels: Record<string, any[]>;
    rules: Record<string, string>;
};

const TRANSITION_WATCHDOG_MS = 10_000;
const HEARTBEAT_MS = 5 * 60 * 1000;
const IS_DEV = (import.meta as any)?.env?.DEV === true;

function logCacheError(scope: string, err: unknown): void {
    console.warn(`[CacheManager] ${scope}:`, err);
}

/* see docs/EXTRACTED_COMMENTS.md#c313 */
export class CacheManager {
    private static _instance: CacheManager | null = null;
    public static get instance(): CacheManager {
        if (!CacheManager._instance) {
            CacheManager._instance = new CacheManager();
        }
        return CacheManager._instance;
    }

    private transitionDepth = 0;
    private transitionWatchdog: ReturnType<typeof setTimeout> | null = null;
    private transitionTrace: { reason: string; stack?: string }[] = [];

    private writeQueue: Promise<void> = Promise.resolve();

    private dirty = false;

    private heartbeatTimer: ReturnType<typeof setInterval> | null = null;
    private liveProbe: (() => boolean) | null = null;
    private snapshotProvider: (() => Promise<ComposeInput | null> | ComposeInput | null) | null = null;
    private mediaPurgeHandler: ((mediaUrls: string[]) => void) | null = null;

    private constructor() {}

    /* --------------------------------------------------------------------
     * Configuration / wiring
     * ----------------------------------------------------------------- */

    /* see docs/EXTRACTED_COMMENTS.md#c314 */
    public configure(opts: {
        isLive: () => boolean;
        snapshotProvider: () => Promise<ComposeInput | null> | ComposeInput | null;
        mediaPurgeHandler?: (mediaUrls: string[]) => void;
    }): void {
        this.liveProbe = opts.isLive;
        this.snapshotProvider = opts.snapshotProvider;
        this.mediaPurgeHandler = opts.mediaPurgeHandler ?? null;
    }

    public isLive(): boolean {
        return this.liveProbe ? this.liveProbe() : false;
    }

    public get transitionInFlight(): boolean {
        return this.transitionDepth > 0;
    }

    /* --------------------------------------------------------------------
     * Transition interlock
     * ----------------------------------------------------------------- */

    public beginTransition(reason: string): void {
        this.transitionDepth++;
        if (IS_DEV) {
            this.transitionTrace.push({ reason, stack: new Error().stack });
        } else {
            this.transitionTrace.push({ reason });
        }
        if (this.transitionDepth === 1) {
            this.armTransitionWatchdog();
        }
    }

    public endTransition(reason: string): void {
        if (this.transitionDepth === 0) {
            console.warn(`[CacheManager] endTransition without matching begin: ${reason}`);
            return;
        }
        this.transitionDepth--;
        this.transitionTrace.pop();
        if (this.transitionDepth === 0) {
            this.clearTransitionWatchdog();
            this.scheduleCommitIfDirty();
        }
    }

    private armTransitionWatchdog(): void {
        this.clearTransitionWatchdog();
        this.transitionWatchdog = setTimeout(() => {
            this.transitionWatchdog = null;
            const open = this.transitionTrace.map((t) => t.reason).join(", ");
            console.error(
                `[CacheManager] transition stuck open for >${TRANSITION_WATCHDOG_MS}ms; force-closing. reasons=[${open}]`
            );
            if (IS_DEV) {
                for (const t of this.transitionTrace) {
                    if (t.stack) console.error(`[CacheManager] opened by ${t.reason} at:\n${t.stack}`);
                }
            }
            this.transitionDepth = 0;
            this.transitionTrace = [];
            this.scheduleCommitIfDirty();
        }, TRANSITION_WATCHDOG_MS);
    }

    private clearTransitionWatchdog(): void {
        if (this.transitionWatchdog != null) {
            clearTimeout(this.transitionWatchdog);
            this.transitionWatchdog = null;
        }
    }

    public markDirty(): void {
        this.dirty = true;
    }

    /* --------------------------------------------------------------------
     * Persist gate (used by canPersistSnapshot)
     * ----------------------------------------------------------------- */

    public canPersist(): boolean {
        return (
            isStructuredCacheEnabled()
            && typeof indexedDB !== "undefined"
            && this.isLive()
            && this.transitionDepth === 0
        );
    }

    /* --------------------------------------------------------------------
     * Snapshot composition + write
     * ----------------------------------------------------------------- */

    /* see docs/EXTRACTED_COMMENTS.md#c315 */
    public async commitSnapshot(reason: string): Promise<CacheSnapshot | null> {
        if (!this.canPersist()) return null;
        if (!this.snapshotProvider) {
            console.warn("[CacheManager] commitSnapshot: no snapshotProvider configured");
            return null;
        }
        let input: ComposeInput | null;
        try {
            input = await Promise.resolve(this.snapshotProvider());
        } catch (e) {
            logCacheError(`compose(${reason})`, e);
            return null;
        }
        if (!input) return null;
        const snap = this.composeSnapshot(input);
        await this.enqueueWrite(snap, reason);
        this.dirty = false;
        this.notifyMediaPurge(snap);
        return snap;
    }

    private composeSnapshot(input: ComposeInput): CacheSnapshot {
        const cloneTransformHash = input.cloneTransformHash || "";
        const signature = buildSignature(input.layoutId, input.hash, cloneTransformHash);
        const deepCopy = <T,>(v: T): T => {
            try {
                return structuredClone(v);
            } catch {
                return JSON.parse(JSON.stringify(v));
            }
        };
        return {
            signature,
            layoutId: input.layoutId,
            hash: input.hash,
            layout: deepCopy(input.layout),
            font_urls: input.font_urls ? deepCopy(input.font_urls) : undefined,
            clone_transform: input.clone_transform ? deepCopy(input.clone_transform) : undefined,
            clone_transforms: input.clone_transforms?.length ? deepCopy(input.clone_transforms) : undefined,
            clone_transform_hash: cloneTransformHash || undefined,
            channels: deepCopy(input.channels),
            rules: deepCopy(input.rules),
            schemaVersion: CACHE_VERSION,
            snapshotTs: Date.now(),
            snapshotTtlMs: SNAPSHOT_TTL_MS,
        };
    }

    private enqueueWrite(snap: CacheSnapshot, reason: string): Promise<void> {
        const next = this.writeQueue.then(() => CacheDatabase.getInstance().putSnapshot(snap));
        this.writeQueue = next.catch((e) => {
            logCacheError(`write(${reason}, sig=${snap.signature})`, e);
        });
        return next;
    }

    /* see docs/EXTRACTED_COMMENTS.md#c316 */
    private scheduleCommitIfDirty(): void {
        if (!this.dirty) return;
        if (!this.canPersist()) return;
        // Microtask to let any synchronous post-transition state stabilize.
        void Promise.resolve().then(() => {
            this.commitSnapshot("post-transition").catch((e) =>
                logCacheError("post-transition commit", e)
            );
        });
    }

    /* --------------------------------------------------------------------
     * Restore
     * ----------------------------------------------------------------- */

    public async restoreLatestSnapshot(layoutId: string): Promise<CacheSnapshot | null> {
        if (!isStructuredCacheEnabled() || typeof indexedDB === "undefined") return null;
        try {
            const candidates = await CacheDatabase.getInstance().getSnapshotsByLayoutId(layoutId);
            const valid = candidates
                .filter(isSnapshotCompatible)
                .filter((s) => !isSnapshotExpired(s));
            if (valid.length === 0) return null;
            valid.sort((a, b) => b.snapshotTs - a.snapshotTs);
            return valid[0];
        } catch (e) {
            logCacheError(`restoreLatestSnapshot(${layoutId})`, e);
            return null;
        }
    }

    /** Hash-takeover lookup; see §16.4 of CACHE_PIPELINE_REDESIGN.md. */
    public async findSnapshotByHash(hash: string): Promise<CacheSnapshot | null> {
        if (!isStructuredCacheEnabled() || typeof indexedDB === "undefined") return null;
        try {
            const candidates = await CacheDatabase.getInstance().getSnapshotsByHash(hash);
            const valid = candidates
                .filter(isSnapshotCompatible)
                .filter((s) => !isSnapshotExpired(s));
            if (valid.length === 0) return null;
            valid.sort((a, b) => b.snapshotTs - a.snapshotTs);
            return valid[0];
        } catch (e) {
            logCacheError(`findSnapshotByHash(${hash})`, e);
            return null;
        }
    }

    /* --------------------------------------------------------------------
     * Heartbeat
     * ----------------------------------------------------------------- */

    public startHeartbeat(): void {
        if (this.heartbeatTimer != null) return;
        this.heartbeatTimer = setInterval(() => {
            if (!this.dirty) return;
            if (!this.canPersist()) return;
            this.commitSnapshot("heartbeat").catch((e) => logCacheError("heartbeat", e));
        }, HEARTBEAT_MS);
    }

    public stopHeartbeat(): void {
        if (this.heartbeatTimer != null) {
            clearInterval(this.heartbeatTimer);
            this.heartbeatTimer = null;
        }
    }

    /* --------------------------------------------------------------------
     * Media SW hook
     * ----------------------------------------------------------------- */

    private notifyMediaPurge(snap: CacheSnapshot): void {
        if (!this.mediaPurgeHandler) return;
        const urls = collectMediaUrls(snap.layout);
        if (urls.length === 0) return;
        try {
            this.mediaPurgeHandler(urls);
        } catch (e) {
            logCacheError("media-purge dispatch", e);
        }
    }
}

/* see docs/EXTRACTED_COMMENTS.md#c317 */
function collectMediaUrls(layout: any): string[] {
    const out = new Set<string>();
    const re = /\.(mp4|webm|mov|jpe?g|png|webp|avif|gif)(\?|#|$)/i;
    const visit = (v: any): void => {
        if (v == null) return;
        if (typeof v === "string") {
            if (re.test(v)) out.add(v);
            return;
        }
        if (Array.isArray(v)) {
            for (const item of v) visit(item);
            return;
        }
        if (typeof v === "object") {
            for (const k of Object.keys(v)) visit(v[k]);
        }
    };
    visit(layout);
    return [...out];
}
